import { useEffect, useRef } from 'react';

import { useWheelStore } from '@store/wheelStore';
import { type WheelProviderProps } from './helpers/wheelContextTypes';

export const WheelProvider: React.FC<WheelProviderProps> = ({
  children,
  streamId,
  hiddenData,
  platformType,
  interactionType,
  startInteraction,
  currentRosResult,
  currentRosFeature,
  interactionState,
  resetResultClicked,
  poolingInteraction,

  setIsStarted,
  updateFSMeta,
  setHiddenData,
  setStartInteraction,
  setInteractionState,
  setResetResultClicked,
  setForPoolingInteraction,
}) => {
  const { isRos } = interactionType || {};
  const { setting, id, metadata, type, color, options } = currentRosFeature || {};
  const isNewWheel = type === 'newWheel' && setting?.wheelSelectionType !== 'customNames';
  const isKeywordEnabled = setting?.enableKeyword;
  const isWheelOptionsEnabled = setting?.enableWheelOptions;

  useEffect(() => {
    const store = useWheelStore.getState();
    const storeId = store.id;
    if (storeId && storeId !== id) {
      store.saveFeatureSnapshot(storeId);
    }
    if (id && storeId !== id) {
      const restored = store.restoreFeatureSnapshot(id);
      if (!restored) useWheelStore.getState().reset(setting);
      useWheelStore.setState({ isExploding: false });
    }
    useWheelStore.getState().syncProps({
      id,
      type,
      color,
      setting,
      metadata,
      rosOptions: options,
      streamId,
      hiddenData,
      platformType,
      interactionState,
      startInteraction,
      currentRosResult,
      currentRosFeature,
      poolingInteraction,
      isRos,
      updateFSMeta,
      setIsStarted,
      setHiddenData,
      setStartInteraction,
      setInteractionState,
      setResetResultClicked,
      setForPoolingInteraction,
    });
    return () => {
      const s = useWheelStore.getState();
      if (s.id) s.saveFeatureSnapshot(s.id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    useWheelStore.getState().syncProps({ streamId, platformType });
  }, [streamId, platformType]);

  useEffect(() => {
    useWheelStore.getState().syncProps({
      currentRosFeature, id, type, color, setting, metadata, rosOptions: options,
    });
  }, [currentRosFeature]);

  useEffect(() => {
    useWheelStore.getState().syncProps({ hiddenData });
  }, [hiddenData]);

  useEffect(() => {
    useWheelStore.getState().syncProps({ interactionState });
  }, [interactionState]);

  useEffect(() => {
    useWheelStore.getState().syncProps({ startInteraction });
  }, [startInteraction]);

  useEffect(() => {
    useWheelStore.getState().syncProps({ poolingInteraction });
  }, [poolingInteraction]);

  useEffect(() => {
    useWheelStore.getState().syncProps({
      updateFSMeta,
      setIsStarted,
      setHiddenData,
      setStartInteraction,
      setInteractionState,
      setResetResultClicked,
      setForPoolingInteraction,
    });
  }, [updateFSMeta, setIsStarted, setHiddenData, setStartInteraction, setInteractionState, setResetResultClicked, setForPoolingInteraction]);

  useEffect(() => {
    if (resetResultClicked) useWheelStore.getState().resetWheel();
  }, [resetResultClicked]);

  const interactionStateMountRef = useRef(true);
  useEffect(() => {
    if (interactionStateMountRef.current) {
      interactionStateMountRef.current = false;
      return;
    }
    if (interactionState !== 'start') return;
    const { opts, options: storeOptions, winnerInfo } = useWheelStore.getState();
    if (winnerInfo?.value) {
      setInteractionState('stopped');
      return;
    }
    if (opts.length + storeOptions.length > 0) {
      useWheelStore.getState().handleSpin();
    }
  }, [interactionState]);

  useEffect(() => {
    useWheelStore.getState().filterHiddenOptions();
  }, [options, hiddenData, id, isNewWheel, isWheelOptionsEnabled]);

  useEffect(() => {
    const isAllInteractions = ['All Interactions', 'allInteractions', '', undefined, 'undefined']
      .includes(setting?.wheelInteractionType);
    if (isAllInteractions || isKeywordEnabled) return;
    setForPoolingInteraction(false);
  }, [setting?.wheelInteractionType, isKeywordEnabled]);

  useEffect(() => {
    useWheelStore.getState().syncProps({ currentRosResult });

    if (currentRosResult[0]?.options?.length > 0) {
      const hiddenIds = hiddenData[id] || [];
      const resultOptions = currentRosResult[0].options;
      const winnerOpt = resultOptions.find((opt: any) => opt.isWinner);
      const filteredOptions = resultOptions.filter((opt: any) => !hiddenIds.includes(opt.id));
      const winnerInfo = winnerOpt
        ? { value: winnerOpt.body, color: winnerOpt.color, id: winnerOpt.id }
        : null;

      if (isNewWheel) {
        useWheelStore.setState({
          opts: filteredOptions,
          wheelId: currentRosResult[0].id,
          winner: null,
          winnerInfo,
        });
      } else {
        useWheelStore.setState({ opts: filteredOptions, winner: null, winnerInfo });
      }
    }
  }, [currentRosResult[0]?.options]);

  const wheelSelectionTypeMountRef = useRef(true);
  useEffect(() => {
    if (wheelSelectionTypeMountRef.current) {
      wheelSelectionTypeMountRef.current = false;
      return;
    }
    if (currentRosResult[0]?.options?.length > 0) return;
    useWheelStore.setState({ winner: null, winnerInfo: null, opts: [] });
  }, [setting?.wheelSelectionType]);

  return <>{children}</>;
};

export default WheelProvider;
